import { useDisabledPageRedirect } from '../../lib/useDisabledPageRedirect';
import { Link } from '../../components/Link';
import { PublicHeader } from '../../components/PublicHeader';
import { PublicFooter } from '../../components/PublicFooter';
import { Button } from '../../components/ui';
import { breadcrumbSchema, faqSchema, Seo } from '../../lib/seo';
import { useFeatures } from '../../lib/useFeatures';

const PARTNER_FAQ = [
  {
    q: 'How much commission do SieveGuard affiliates earn?',
    a: 'You earn a percentage of every payment a referred client makes — a standard rate on monthly plans and a higher rate on annual plans — for their first 12 months as a paying customer.',
  },
  {
    q: 'Is the commission recurring?',
    a: 'Yes. You earn on each renewal a referred client pays during their first year, not just their first payment.',
  },
  {
    q: 'When do I get paid?',
    a: "Commission is released once a referred client stays subscribed past the hold period, so it reflects retained customers. Payouts are then processed manually to the payout details on your account.",
  },
  {
    q: 'How are referrals tracked?',
    a: 'Every visit through your referral link is attributed to you for 60 days. If they sign up and subscribe in that window, the referral and its commission show up in your partner dashboard.',
  },
];

const STEPS = [
  {
    n: '1',
    title: 'Apply & get approved',
    body: 'Submit a short application. Once our team approves you, your unique referral code and dashboard go live.',
  },
  {
    n: '2',
    title: 'Share your link',
    body: 'Promote SieveGuard anywhere with your link. Every signup that comes through it is attributed to you for 60 days.',
  },
  {
    n: '3',
    title: 'Earn recurring commission',
    body: 'When a referral subscribes to a paid plan, you earn commission on every payment for their first year — released once they stay past the hold period.',
  },
];

export function PartnersPage() {
  const { feature, isLoading: featuresLoading } = useFeatures();
  // Program globally disabled → keep the marketing page off the public surface. Effect-based so
  // the prerender never bakes a redirect stub (see useDisabledPageRedirect).
  useDisabledPageRedirect(!featuresLoading && !feature('affiliate'));
  // Render nothing once the LIVE flag says the affiliate program is off, so an in-app navigation
  // doesn't flash the page before the redirect fires (same guard as the About page). Prerender-safe:
  // the flag defaults ENABLED at build, so this is false there and the full page still prerenders.
  if (!featuresLoading && !feature('affiliate')) return null;
  return (
    <>
      <Seo
        title="Affiliate program"
        description="Become a SieveGuard partner. Earn recurring commission on every client you refer, monthly or yearly, for their first year. Transparent tracking, no caps."
        path="/partners"
        // noindex when the program is off (in addition to the redirect below) so a disabled
        // partner page never lingers in the index.
        index={feature('affiliate')}
        keywords="affiliate program, referral program, partner program, email deliverability affiliate, recurring commission"
        jsonLd={[
          faqSchema(PARTNER_FAQ),
          breadcrumbSchema([
            { name: 'Home', url: 'https://sieveguard.com/' },
            { name: 'Partners', url: 'https://sieveguard.com/partners' },
          ]),
        ]}
      />
    <div className="mx-auto max-w-[1210px] px-6 pb-10">
      <PublicHeader />

      <section className="mx-auto max-w-2xl py-16 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-accentdeep/30 bg-accentdeep/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-accent">
          Affiliate program
        </span>
        <h1 className="mt-5 font-display text-4xl font-extrabold tracking-[-0.02em] md:text-5xl">
          Earn for every client you send our way
        </h1>
        <p className="mx-auto mt-4 max-w-lg text-[15px] leading-relaxed text-muted">
          Refer businesses to SieveGuard and earn recurring commission on their subscription —
          monthly and yearly — for a full year. Transparent tracking, manual payouts, no caps on
          how many clients you bring.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link to="/partners/apply">
            <Button variant="primary" className="h-12 px-6">
              Become a partner
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="h-12 px-6">
              Partner sign in
            </Button>
          </Link>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {STEPS.map((s) => (
          <div key={s.n} className="rounded-2xl border border-bordersoft bg-panel p-6">
            <div className="grid h-9 w-9 place-items-center rounded-full bg-accent/15 font-mono text-sm font-bold text-accent">
              {s.n}
            </div>
            <h2 className="mt-4 text-base font-semibold">{s.title}</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{s.body}</p>
          </div>
        ))}
      </section>

      <section className="mt-12 rounded-2xl border border-bordersoft bg-gradient-to-b from-panel to-panel2 p-10 text-center">
        <h2 className="font-display text-2xl font-bold tracking-tight">How commission works</h2>
        <div className="mx-auto mt-5 grid max-w-2xl gap-4 text-left sm:grid-cols-2">
          {[
            ['Recurring, first year', 'You earn on every payment a referred client makes for their first 12 months — not just the first one.'],
            ['Monthly & yearly', 'Both plans qualify. Yearly subscriptions earn a higher rate, paid on the upfront annual amount.'],
            ['Released after the hold', "Commission unlocks once the client stays subscribed past the hold period, so it reflects real, retained customers."],
            ['Clear tracking', 'Your dashboard shows signups, who subscribed, days active, and exactly when each commission is released.'],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-bordersoft bg-bg/40 p-4">
              <div className="text-[13.5px] font-semibold text-txt">{t}</div>
              <p className="mt-1 text-[13px] leading-relaxed text-muted">{b}</p>
            </div>
          ))}
        </div>
        <Link to="/partners/apply" className="mt-7 inline-block">
          <Button variant="primary" className="h-12 px-6">
            Apply now
          </Button>
        </Link>
      </section>

      <section className="mx-auto mt-12 max-w-2xl">
        <h2 className="text-center font-display text-2xl font-bold tracking-tight">
          Frequently asked questions
        </h2>
        <div className="mt-6 flex flex-col gap-3">
          {PARTNER_FAQ.map((item) => (
            <details key={item.q} className="group rounded-xl border border-bordersoft bg-panel p-4">
              <summary className="cursor-pointer list-none text-[14px] font-semibold text-txt marker:hidden">
                {item.q}
              </summary>
              <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
    </>
  );
}

// Default export required by React Router framework-mode route modules.
export default PartnersPage;
